<template>
  <main class="flyer-page print-friendly">
    <div class="flyer-container">
      <!-- Header -->
      <header class="flyer-header">
        <div class="logo-section">
          <img src="/assets/img/logo.png" alt="Space Coast STEM Logo" class="logo" />
        </div>
        <h1>Your Old Laptops Can Change Lives</h1>
        <p class="tagline">A Request from Your Team to Support Local Students</p>
      </header>

      <!-- Direct Ask -->
      <section class="ask-section">
        <div class="ask-box">
          <h2>Dear IT Department,</h2>
          <p>We're reaching out on behalf of <strong>Space Coast STEM</strong>, a local nonprofit working to provide laptops to students in Brevard County who don't have access to technology at home.</p>
          <p><strong>We'd like to ask:</strong> Would you consider donating decommissioned laptops from your IT closet instead of recycling them?</p>
        </div>
      </section>

      <!-- The Impact -->
      <!-- <section class="impact-section">
        <h2>The Impact</h2>
        <div class="stat-row">
          <div class="stat-box single">
            <span class="stat-number">Every Laptop</span>
            <p>can help a student complete homework and develop digital skills</p>
          </div>
        </div>
      </section> -->

      <!-- What We Do -->
      <section class="process-section">
        <h2>What We Do With Donated Laptops</h2>
        <div class="process-steps">
          <div class="process-item">
            <div class="process-icon">1</div>
            <div class="process-content">
              <strong>Wipe & Refurbish</strong>
              <p>We evaluate hardware, repair what we can, wipe hard drives and install a new OS</p>
            </div>
          </div>
          <div class="process-item">
            <div class="process-icon">2</div>
            <div class="process-content">
              <strong>Work with Teachers</strong>
              <p>We partner with local teachers to identify students who need technology at home</p>
            </div>
          </div>
          <div class="process-item">
            <div class="process-icon">3</div>
            <div class="process-content">
              <strong>Deliver to Students</strong>
              <p>Laptops are given directly to students at no cost through trusted school channels</p>
            </div>
          </div>
        </div>
      </section>

      <!-- What We Accept -->
      <section class="accept-section">
        <h2>What We Can Accept</h2>
        <div class="accept-grid">
          <div class="accept-item requirements">
            <strong>Minimum Requirements</strong>
            <ul>
              <li>Working screen & battery</li>
              <li>4GB RAM or more</li>
              <li>With or without hard drive/SSD</li>
            </ul>
            <p class="note">We can install fresh storage drives if needed</p>
          </div>
          <div class="accept-item also">
            <strong>We Also Accept</strong>
            <ul>
              <li>Power adapters & chargers</li>
              <li>Laptop bags & accessories</li>
              <li>Computer mice</li>
            </ul>
          </div>
        </div>
        <!-- <div class="linux-info">
          <strong>Why Linux?</strong>
          <p>We install lightweight Linux distributions that breathe new life into older hardware, making 5-10 year old laptops run smoothly and securely without licensing costs.</p>
        </div> -->
      </section>

      <!-- Data Security -->
      <section class="security-section">
        <h2>Data Security & Compliance</h2>
        <div class="security-content">
          <p>We understand the importance of data security. We recommend that you:</p>
          <ul>
            <li><strong>Wipe drives</strong> using your standard procedures before donation</li>
            <li><strong>Remove asset tags</strong> or identifying information if needed</li>
            <li>Provide laptops <strong>"as-is"</strong> - we handle all refurbishment</li>
          </ul>
          <p class="note">We completely reformat all drives and install fresh operating systems, but we encourage you to follow your organization's data sanitization policies before donation.</p>
        </div>
      </section>

      <!-- Tax Benefits -->
      <section class="tax-section">
        <h2>Benefits for Your Organization</h2>
        <div class="benefits-grid">
          <div class="benefit-item">
            <strong>Tax Deduction</strong>
            <p>As a registered 501(c)(3) nonprofit, we can provide donation receipts for tax purposes</p>
          </div>
          <div class="benefit-item">
            <strong>Community Impact</strong>
            <p>Support local students and demonstrate corporate social responsibility</p>
          </div>
          <div class="benefit-item">
            <strong>Easy Process</strong>
            <p>We can arrange pickup or drop-off to make donation convenient</p>
          </div>
        </div>
      </section>

      <!-- CTA Footer -->
      <footer class="flyer-footer">
        <h2>Ready to Help?</h2>
        <p class="footer-text">We'd love to work with your organization to give old laptops a second life helping local students.</p>
        <div class="contact-info">
          <div class="contact-item">
            <strong>Website:</strong> spacecoaststem.org
          </div>
          <div class="contact-item">
            <strong>Email:</strong> <a href="mailto:donation@spacecoaststem.org">Donations@spacecoaststem.org</a>
          </div>
          <div class="contact-item">
            <strong>Learn More:</strong> <a href="https://spacecoaststem.org/donate">spacecoaststem.org/donate</a>
          </div>
        </div>
        <p class="thank-you">Thank you for considering this opportunity to make a difference in our community!</p>
      </footer>
    </div>
  </main>
</template>

<script setup lang="ts">
// Print-friendly static page for IT departments
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

.flyer-page {
  font-family: 'Inter', sans-serif;
  background: white;
  min-height: 100vh;
}

.flyer-container {
  max-width: 8.5in;
  margin: 0 auto;
  padding: 0.5in;
  background: white;
}

/* Header */
.flyer-header {
  text-align: center;
  margin-bottom: 1.25rem;
  border-bottom: 3px solid #2c4a6b;
  padding-bottom: 1rem;
}

.logo-section {
  margin-bottom: 0.75rem;
}

.logo {
  height: 60px;
  width: auto;
}

.flyer-header h1 {
  font-size: 1.75rem;
  font-weight: 800;
  color: #2c4a6b;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.tagline {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-style: italic;
}

/* Ask Section */
.ask-section {
  margin-bottom: 1.25rem;
}

.ask-box {
  background: #f5f7fa;
  padding: 1.25rem;
  border-radius: 8px;
  border-left: 4px solid #3ba3d1;
}

.ask-box h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
}

.ask-box p {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #333;
  margin: 0 0 0.75rem 0;
}

.ask-box p:last-child {
  margin-bottom: 0;
  font-weight: 600;
  color: #2c4a6b;
}

/* Impact Section */
.impact-section {
  margin-bottom: 1.25rem;
}

.impact-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e94f3a;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stat-box.single {
  grid-column: span 2;
  max-width: 500px;
  margin: 0 auto;
}

.stat-box {
  background: linear-gradient(135deg, #2c4a6b 0%, #3ba3d1 100%);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 800;
  display: block;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.stat-box p {
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
  opacity: 0.95;
}

/* Process Section */
.process-section {
  margin-bottom: 1.25rem;
}

.process-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.process-steps {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.process-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f5f7fa;
  border-radius: 6px;
}

.process-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #3ba3d1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.process-content {
  flex: 1;
}

.process-content strong {
  display: block;
  font-size: 0.95rem;
  color: #2c4a6b;
  margin-bottom: 0.25rem;
}

.process-content p {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* Accept Section */
.accept-section {
  margin-bottom: 1.25rem;
}

.accept-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.accept-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.accept-item {
  padding: 1rem;
  border-radius: 8px;
}

.accept-item.requirements {
  background: #e8f5e9;
  border-left: 4px solid #4caf50;
}

.accept-item.also {
  background: #f5f7fa;
  border-left: 4px solid #3ba3d1;
}

.linux-info {
  margin-top: 1rem;
  padding: 1rem;
  background: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.linux-info strong {
  display: block;
  font-size: 1rem;
  color: #2c4a6b;
  margin-bottom: 0.5rem;
}

.linux-info p {
  font-size: 0.85rem;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

.accept-item strong {
  display: block;
  font-size: 1rem;
  color: #2c4a6b;
  margin-bottom: 0.5rem;
}

.accept-item ul {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.85rem;
  color: #333;
}

.accept-item ul li {
  margin-bottom: 0.25rem;
  line-height: 1.4;
}

.accept-item p {
  font-size: 0.85rem;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

/* Security Section */
.security-section {
  margin-bottom: 1.25rem;
}

.security-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.security-content {
  background: #fff3e0;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #ff9800;
}

.security-content > p {
  font-size: 0.9rem;
  color: #333;
  margin: 0 0 0.5rem 0;
}

.security-content ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  font-size: 0.85rem;
  color: #333;
}

.security-content ul li {
  margin-bottom: 0.25rem;
  line-height: 1.4;
}

.note {
  font-size: 0.8rem !important;
  font-style: italic;
  color: #666 !important;
  margin-top: 0.75rem !important;
}

/* Tax Section */
.tax-section {
  margin-bottom: 1.25rem;
}

.tax-section h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.benefit-item {
  background: #f5f7fa;
  padding: 0.75rem;
  border-radius: 6px;
  text-align: center;
}

.benefit-item strong {
  display: block;
  font-size: 0.9rem;
  color: #2c4a6b;
  margin-bottom: 0.5rem;
}

.benefit-item p {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* Footer */
.flyer-footer {
  border-top: 3px solid #2c4a6b;
  padding-top: 1rem;
  text-align: center;
}

.flyer-footer h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e94f3a;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-text {
  font-size: 0.95rem;
  color: #333;
  margin: 0 0 1rem 0;
}

.contact-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.contact-item {
  font-size: 0.9rem;
  color: #333;
}

.contact-item strong {
  color: #2c4a6b;
}

.thank-you {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c4a6b;
  margin: 1rem 0 0 0;
  font-style: italic;
}

/* Print Styles */
@media print {
  .flyer-page {
    background: white;
  }

  .flyer-container {
    padding: 0.25in;
    max-width: none;
  }

  @page {
    margin: 0.25in;
    size: letter portrait;
  }

  /* Preserve colors and backgrounds */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Prevent page breaks inside elements */
  .ask-box,
  .process-item,
  .accept-item,
  .security-content,
  .benefit-item,
  .stat-box {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Prevent page breaks after headers */
  h2 {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* Preserve grid layouts */
  .stat-row,
  .accept-grid,
  .benefits-grid,
  .process-steps {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Ensure contact info stays together */
  .contact-info {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Preserve flexbox and grid layouts */
  .process-steps,
  .contact-info {
    display: flex !important;
  }

  .stat-row,
  .accept-grid,
  .benefits-grid {
    display: grid !important;
  }
}

/* Responsive for screen viewing */
@media (max-width: 768px) {
  .flyer-container {
    padding: 1rem;
  }

  .stat-row,
  .accept-grid,
  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .contact-info {
    flex-direction: column;
    gap: 0.5rem;
  }

  .flyer-header h1 {
    font-size: 1.5rem;
  }
}
</style>
